import { AnimatedSprite, Assets, Cache, Container, Sprite, Spritesheet, Texture } from "pixi.js";

import { Battery, RECOIL_TRAVEL } from "../../../shared/Aiming";
import { EmblemFitting } from "../../../shared/BoatTree";
import { DEG_TO_RAD, Fitout } from "../../../shared/Fitout";

/**
 * One ship's deck, assembled out of the sheets: hull, deck furniture, guns, canvas.
 *
 * It is its own module rather than part of [Boat.ts](Boat.ts) because more than one thing builds
 * one. A boat in the sea is the obvious one; the other is a **card**, drawn once and lifted into
 * a `<canvas>` with `renderer.extract` — that was the upgrade buttons, and it will be the shop's
 * boat cards (design/Shop.md, design/UI.md). Both have to compose the same ship out of the same
 * art, and the second copy of this is the one that would quietly stop matching the first.
 *
 * Ordering, per design/Evolution.md: parts over the base hull in list order, guns over the
 * parts, canvas over everything — a sail is the tallest thing on a ship.
 */

/**
 * Frame in the `boats` sheet drawn when the tree names hull art that is not in the sheets.
 * A broken reference is a tree mid-edit rather than a crash — the dashboard says so on the
 * level; here it is a boat that is visibly the wrong one instead of an empty patch of sea.
 */
export const FALLBACK_HULL = "TestBoat";

/** The sheets a level's art may live in. Frame names are namespaced, so nothing collides. */
const ART_SHEETS = ["boats", "sails", "emblems"] as const;

/**
 * The layers a captain's colours are painted on, derived from an art reference by name.
 *
 * `npm run import-boat` emits them beside the combined art it also still ships, so
 * `config/boat.json` keeps naming `sails/plain_small_sail` and this works out the rest. A rig or a
 * hull whose layers are not in the sheets falls back to the combined frame, untinted, which is what
 * makes the whole thing incremental: art that has not been through the importer still draws.
 *
 * See design/Art.md §4.
 */
const LAYER = {
    /** The cloth. Greyscale, and the only thing `sailTint` touches. */
    canvas: (art: string) => `${art}_canvas`,
    /** The rig drawn *behind* the cloth. Full colour, never tinted. */
    spar: (art: string) => `${art}_spar`,
    /** The rig drawn *in front of* it — the boom a furled sail rolls up behind. */
    over: (art: string) => `${art}_over`,
    /** Timber. Greyscale, and what `hullTint` picks the wood of. */
    wood: (art: string) => `${art}_wood`
};

/**
 * One gun on a built deck, kept apart from the rest of the furniture because it is the only
 * thing on a ship that turns independently of the hull. See `train` below.
 */
export interface DeckGun {
    /** Which cannon slot it sits in — what a `Battery` files its offset under. */
    index: number;
    sprite: Sprite;
    /** The bearing its slot was authored at, in radians. Where the barrel sits with no aim on it. */
    rest: number;
    /**
     * Where its slot puts it on the deck, in hull-local pixels. Held because a recoil moves the
     * gun off that point and has to have somewhere to put it back — the sprite's own position
     * stops being the answer the moment it is fired.
     */
    restX: number;
    restY: number;
    /** How far back a full recoil throws this gun, in hull-local pixels. See RECOIL_TRAVEL. */
    travel: number;
}

/**
 * One sail on a built deck. Kept apart for the same reason a gun is: it turns independently of
 * the hull, and it is the one fitting whose *art* has to say what the ship is doing. See `trim`.
 */
export interface DeckSail {
    /**
     * Everything this sail is, at its slot. **The container is what turns and fades**, not the
     * sprites in it: a sail is up to four layers now, and bracing four things separately is four
     * chances for them to drift apart by a frame or a radian.
     */
    container: Container;
    /**
     * The cloth. The layer `sailTint` colours, the one `flat` is measured on, and — for art the
     * importer has not split — the whole sail.
     */
    canvas: Sprite;
    /**
     * The rig behind the cloth and the rig in front of it, or null for art with no split. Both
     * step with the billow; neither is ever tinted, because a captain picks the canvas and not
     * the boom. `spar` is legitimately empty on a frame whose rig is entirely in front — see
     * design/Art.md §4.
     */
    spar: Sprite | null;
    over: Sprite | null;
    /** The device this sail flies, or null for a plain one. See `DeckEmblem`. */
    emblem: DeckEmblem | null;
    /** The bearing its spar was authored at, in radians. The brace angle is added to this. */
    rest: number;
    /**
     * The alpha this sail was built at. Held because luffing dims a sail, and multiplying is what
     * stops that from clobbering the dimming an upgrade button already applied.
     */
    alpha: number;
    /**
     * Which of its frames is the flattest canvas, or -1 for art with only one. Found by measuring
     * rather than assumed to be the last, so a rig authored full-first and one authored flat-first
     * both luff correctly. See `trim`.
     */
    flat: number;
}

/**
 * A device painted on a sail, and what it needs to stay painted on it.
 *
 * **It rides the cloth.** The catalogue's `width`, `cx` and `cy` are fractions of a sail's box,
 * and a sail's box is a different height on every frame of its billow — so the device is re-fitted
 * to whichever frame the canvas is on rather than pinned to the first one. Pinning it was tried
 * and is wrong: the flattest frame of the shipped rigs is 89 pixels against the fullest 154, so a
 * device placed 73% up the full box floats clean off a furled sail onto the deck.
 *
 * The rule is the artist's own, read back off the art: the device sits at (`cx`, `cy`) of the
 * frame being drawn, and scales with that frame's height against the first frame's. On
 * `small_skull_sail_02` the artist drew the skull at 0.5366 of the box where the full sail has it
 * at 0.6309 — and 131/154 x 0.6309 is 0.5367. See design/Art.md §7.
 */
export interface DeckEmblem {
    sprite: Sprite;
    /** Where the catalogue says it sits, as fractions of a sail's box. */
    fitting: EmblemFitting;
    /** The untrimmed height of the cloth's **first** frame, which its size is measured against. */
    fullHeight: number;
}

/** A built deck, and the fittings on it whose art moves and therefore needs a clock. */
export interface Deck {
    container: Container;
    /** Every animated fitting — the raised canvas today — for whoever is stepping the frames. */
    animated: AnimatedSprite[];
    /** The guns, in slot order, for whoever is swinging them onto a target. */
    guns: DeckGun[];
    /** The canvas, in slot order, for whoever is bracing it round to the wind. */
    sails: DeckSail[];
}

/**
 * Below this much of the wind, canvas is drawn on its flattest frame instead of billowing: the
 * sail is shaking rather than pulling. Well inside the dead sector's edge, so a hull that is
 * genuinely close-hauled still draws a full sail — being slow is not the same as luffing.
 */
const LUFF_POWER = 0.06;
/**
 * The band over which a sail fades as it loses the wind, and how pale it gets at the bottom of
 * it. Wider than LUFF_POWER on purpose: the colour should drain gradually as the ship comes up
 * into the wind, and only the *shape* should change suddenly, because that is the moment the
 * canvas actually stops working.
 */
const LUFF_FADE = 0.25;
const LUFF_ALPHA = 0.45;

/**
 * Builds the whole assembly, centred on the hull's own centre and facing north — bow at the top
 * of the frame, the zero a heading uses.
 *
 * Every fitting is placed against **this** ship's size (`fitout.size`), which is its preset's
 * authored length times the scale its level has grown it to — never the starter's. So a slot
 * dragged in the dashboard lands in the same place on every boat and at every size, and the gun
 * a client draws sits exactly where the server thinks its muzzle is.
 */
export function buildDeck(fitout: Fitout): Deck {
    const container = new Container();
    const animated: AnimatedSprite[] = [];
    const guns: DeckGun[] = [];
    const sails: DeckSail[] = [];
    const { size } = fitout;

    // The timber layer if the importer has emitted one, and then the captain's wood; the combined
    // hull untinted if it has not. The two share a box, so the length below holds either way.
    const hullArt = fitout.preset?.frame ?? FALLBACK_HULL;
    const timber = framesOf(LAYER.wood(hullArt));
    const hull = new Sprite({
        texture: (timber.length > 0 ? timber : framesOf(hullArt))[0] ?? Texture.from(FALLBACK_HULL),
        anchor: 0.5
    });
    if (timber.length > 0) hull.tint = fitout.hullTint;
    // texture.height is the untrimmed frame height, so the length holds for any hull
    // regardless of how tightly the packer cropped it.
    hull.scale.set(size.length / hull.texture.height);
    container.addChild(hull);

    for (const part of fitout.preset?.parts ?? []) {
        add(container, animated, part.frame, part, drawnSize(fitout, part.size, 1), size, 0.5, 1);
    }

    for (const gun of fitout.cannons) {
        const sprite = add(container, animated, gun.fitting.frame, gun.slot, drawnSize(fitout, gun.fitting.size, gun.slot.scale), size, 0.5, 1);
        // Art the sheets do not have draws nothing and therefore trains nothing — a tree
        // mid-edit is a bare rail, not a crash. See `FALLBACK_HULL`.
        if (sprite) {
            guns.push({
                index: gun.index,
                sprite,
                rest: gun.slot.rotation * DEG_TO_RAD,
                restX: sprite.x,
                restY: sprite.y,
                // Measured against the gun as it is actually drawn, which is the same length
                // `Cannon.muzzleAcross` is a fraction of — see `drawnSize`.
                travel: drawnSize(fitout, gun.fitting.size, gun.slot.scale) * RECOIL_TRAVEL
            });
        }
    }

    // **Only the largest sail in a rig carries a device.** A skull on a forty-pixel staysail is
    // noise on a screen that has to be read in a quarter-second (design/Pitch.md), and a ship
    // flying the same emblem three times reads as a texture rather than as a flag.
    const flagship = largestSail(fitout);

    for (const sail of fitout.sails) {
        const built = addSail(container, animated, fitout, sail, size, sail === flagship ? fitout.emblem : null);
        // Art the sheets do not have draws nothing and therefore braces nothing, exactly as a
        // missing gun trains nothing.
        if (built) sails.push(built);
    }

    const deck = { container, animated, guns, sails };
    // Fitted to the frame the cloth starts on, so a deck that is drawn once and never trimmed —
    // a shop card — still has its device in the right place.
    dressEmblems(deck);

    return deck;
}

/** One sail on a fitout — the rig entry plus the slot it hangs on. */
type FittedSail = Fitout["sails"][number];

/**
 * Which sail on a ship a device goes on: the biggest as actually drawn, ties to the first in the
 * rig's own order. Null for a ship carrying no canvas at all.
 */
function largestSail(fitout: Fitout): FittedSail | null {
    let biggest: FittedSail | null = null;
    for (const sail of fitout.sails) {
        const drawn = drawnSize(fitout, sail.fitting.size, sail.slot.scale);
        if (!biggest || drawn > drawnSize(fitout, biggest.fitting.size, biggest.slot.scale)) biggest = sail;
    }

    return biggest;
}

/**
 * One sail, hung on the deck as its layers: **spar, then canvas, then device, then over.**
 *
 * The order is measured rather than assumed — design/Art.md §4 — and the
 * device sits between the cloth and the boom because that is where paint on a sail is: over the
 * canvas it is painted on, under the spar that crosses in front of it.
 *
 * Everything goes in a container placed at the slot, so bracing and fading are one assignment each
 * rather than four. Canvas is pinned by its bottom edge, which is where the artist put the spar:
 * only the billow grows, so anchoring the middle would pump the whole sail on its slot instead of
 * bellying it toward the bow. It is also the point the yard braces round — see `trim`.
 *
 * A rig the importer has not split falls back to the combined frame, untinted. That fallback is
 * what lets the art arrive a sheet at a time instead of on a flag day.
 */
function addSail(
    parent: Container,
    animated: AnimatedSprite[],
    fitout: Fitout,
    sail: FittedSail,
    size: { halfBeam: number; halfLength: number },
    emblem: EmblemFitting | null
): DeckSail | null {
    const art = sail.fitting.frame;
    const length = drawnSize(fitout, sail.fitting.size, sail.slot.scale);
    const split = framesOf(LAYER.canvas(art)).length > 0;

    const canvas = layer(animated, split ? LAYER.canvas(art) : art, length);
    if (!canvas) return null;
    if (split) canvas.tint = fitout.sailTint;

    const container = new Container();
    container.position.set(sail.slot.x * size.halfBeam, sail.slot.y * size.halfLength);
    container.rotation = sail.slot.rotation * DEG_TO_RAD;

    const spar = split ? layer(animated, LAYER.spar(art), length) : null;
    const over = split ? layer(animated, LAYER.over(art), length) : null;
    const device = split && emblem ? placeEmblem(emblem, canvas, fitout.emblemTint) : null;

    for (const piece of [spar, canvas, device?.sprite, over]) if (piece) container.addChild(piece);
    parent.addChild(container);

    return {
        container,
        canvas,
        spar,
        over,
        emblem: device,
        rest: sail.slot.rotation * DEG_TO_RAD,
        alpha: 1,
        flat: flattestFrame(split ? LAYER.canvas(art) : art)
    };
}

/**
 * One layer of a sail, at the sail's own scale and pinned by its bottom edge, with no position or
 * rotation of its own — the container it goes in carries those.
 *
 * **Every layer is scaled off its own first frame**, which comes to the same number for all of
 * them because the importer emits a layer on the same box as the art it was cut from. That is a
 * contract and not a coincidence: `import-boat` refuses to write a derived frame whose box moved.
 */
function layer(animated: AnimatedSprite[], art: string, length: number): Sprite | null {
    const frames = framesOf(art);
    if (frames.length === 0) return null;

    const anchor = { x: 0.5, y: 1 };
    // Driven by hand rather than by autoUpdate, for the same reason `place` is: `Ticker.shared`
    // is not the clock this game runs on.
    const sprite =
        frames.length > 1
            ? new AnimatedSprite({ textures: frames, anchor, autoUpdate: false })
            : new Sprite({ texture: frames[0], anchor });

    sprite.scale.set(length / frames[0].height);
    if (sprite instanceof AnimatedSprite) animated.push(sprite);

    return sprite;
}

/**
 * The device on a sail: sized and positioned as fractions of the sail's **first frame's box**,
 * which is what lets one catalogue entry place the same emblem on every rig without a table per
 * preset.
 *
 * It is a plain `Sprite` and deliberately not in `animated`. A device painted on canvas does not
 * billow with it — the cloth moves behind a thing that stays put — and stepping it with the sail
 * would need three frames of art nobody drew.
 */
function placeEmblem(emblem: EmblemFitting, canvas: Sprite, tint: number): DeckEmblem | null {
    const frames = framesOf(emblem.frame);
    if (frames.length === 0) return null;

    return {
        sprite: new Sprite({ texture: frames[0], anchor: 0.5, tint }),
        fitting: emblem,
        // `texture.height` is the untrimmed frame, which is the box the importer measured against.
        fullHeight: canvas.texture.height
    };
}

/**
 * Re-fits every device on a deck to the frame its cloth is currently on.
 *
 * **Call it after anything that changes a sail's frame** — the billow clock and the drop onto a
 * luffing sail's flat frame are the two, and both do. It is idempotent and a handful of
 * multiplications per device, so calling it twice in a frame costs nothing and forgetting it once
 * leaves a skull hanging over the deck.
 */
export function dressEmblems(deck: Deck): void {
    for (const sail of deck.sails) {
        if (!sail.emblem) continue;

        const { sprite, fitting, fullHeight } = sail.emblem;
        const frame = sail.canvas.texture;
        const scale = sail.canvas.scale.x;
        // The cloth is anchored bottom-centre on the container's origin, so this frame's box runs
        // from -width/2 to +width/2 across and from -height up to 0 down.
        const boxWidth = frame.width * scale;
        const boxHeight = frame.height * scale;

        sprite.position.set((fitting.cx - 0.5) * boxWidth, (fitting.cy - 1) * boxHeight);
        // Shrunk with the billow: a device on furled canvas is a device on less canvas.
        sprite.scale.set((fitting.width * boxWidth * (frame.height / fullHeight)) / sprite.texture.width);
    }
}

/**
 * Puts every gun on a deck where `battery` says it is: turned to whatever bearing it has swung
 * to, and slid back down its own barrel by however much of its recoil is left to run.
 *
 * Called each frame by whoever owns the boat, for the local hull and every other one alike — a
 * rival's barrels coming round is the same picture drawn from the same integration, run off
 * `Ship.aim` instead of off a pointer. See shared/Aiming.ts.
 *
 * The rotation is the slot's bearing plus the battery's offset for that slot, which is the same
 * sum the server does before a ball leaves the muzzle. A gun the battery has never heard of
 * reads zero for both and sits at rest, which is what an unaimed hull looks like.
 *
 * **The recoil runs along the gun, not along the hull.** A ball leaves at `(sin, -cos)` of the
 * bearing — the convention `advance` sails on — so the carriage goes the other way, and it does
 * so in the turned frame rather than the deck's: a gun trained forward on a port rail kicks
 * aft-ish along its own barrel, not sideways into the sea.
 */
export function train(deck: Deck, battery: Battery): void {
    for (const gun of deck.guns) {
        const bearing = gun.rest + battery.offsetAt(gun.index);
        gun.sprite.rotation = bearing;

        const back = battery.recoilOf(gun.index) * gun.travel;
        gun.sprite.position.set(gun.restX - Math.sin(bearing) * back, gun.restY + Math.cos(bearing) * back);
    }
}

/**
 * Braces every sail on a deck round to the wind, and says whether the canvas is pulling.
 *
 * `brace` is how far the yards are turned off square, in radians, in the hull's own frame — the
 * caller works it out from the point of sail (`Boat.trimTo`), because the angle is a property of
 * the ship's heading against the weather rather than of the deck. Zero is squared across the beam,
 * which is the rest pose the art is drawn in and exactly right for a hull running dead downwind.
 *
 * `power` is what `sailPower` says this heading collects, and it drives the two things that make
 * a sail *readable* rather than merely turned:
 *
 * - **the canvas fades** as it loses the wind, over LUFF_FADE, so a ship coming up into the wind
 *   visibly goes pale before it goes slow;
 * - **it drops onto its flattest frame** under LUFF_POWER, which stops the billow dead. Shape is
 *   the loud signal and colour is the quiet one, so the sudden change is saved for the moment the
 *   canvas genuinely stops working.
 *
 * Every hull in the sea gets this, not just the player's: a rival's sails say what their ship is
 * about to do a good deal earlier than their wake does. See design/UI.md.
 */
export function trim(deck: Deck, brace: number, power: number): void {
    const fade = LUFF_ALPHA + (1 - LUFF_ALPHA) * Math.min(1, power / LUFF_FADE);
    const luffing = power < LUFF_POWER;

    for (const sail of deck.sails) {
        // Set on the container, so every layer braces and fades as one thing. Tint is orthogonal
        // to alpha, so a coloured sail luffs for free.
        sail.container.rotation = sail.rest + brace;
        // Multiplied rather than assigned: `DeckSail.alpha` is whatever the deck was built at, so
        // a card drawing a ship faded stays faded while its canvas luffs.
        sail.container.alpha = sail.alpha * fade;

        // The luff frame goes on the cloth and on the rig either side of it **from the same
        // index**, so they cannot drift apart onto different frames of the same sail.
        if (luffing && sail.flat >= 0) {
            for (const piece of [sail.canvas, sail.spar, sail.over]) {
                if (piece instanceof AnimatedSprite) piece.currentFrame = sail.flat;
            }
        }
    }

    // The frame may have just moved under every device on this deck.
    dressEmblems(deck);
}

/**
 * Which frame of an art reference is the flattest canvas, by untrimmed height, or -1 if it has
 * only one. The shipped rigs run 154, 131 and 89 pixels tall, so this is the last of them — but
 * it is **measured rather than assumed**, because nothing about the sheet promises the animation
 * was authored full-first and a rig that luffed by billowing would be a strange bug to find.
 */
function flattestFrame(art: string): number {
    const frames = framesOf(art);
    if (frames.length < 2) return -1;

    let flat = 0;
    for (let i = 1; i < frames.length; i++) if (frames[i].height < frames[flat].height) flat = i;

    return flat;
}

/**
 * One fitting on its own, at its art's natural size and facing north — a gun, a sail, a crate.
 *
 * What a card shows when the question is "what kind of thing is this" rather than "where does it
 * sit on a deck": a cannon reads as a cannon at 72 pixels, while a whole ship with one lit gun
 * does not. Its counterpart is `buildDeck`, which answers the where.
 *
 * Null for art the sheets do not have, same as everywhere else — see `FALLBACK_HULL`.
 */
export function buildFitting(art: string): Container | null {
    const frames = framesOf(art);
    if (frames.length === 0) return null;

    const container = new Container();
    container.addChild(new Sprite({ texture: frames[0], anchor: 0.5 }));

    return container;
}

/**
 * How big a fitting is actually drawn: its own authored size, nudged by its slot, then grown with
 * the hull.
 *
 * **The hull's scale is the part that is easy to leave out.** A slot is a *fraction* of the hull
 * and a fitting's `size` is in *pixels*, so without it a boat that doubled in length would carry
 * the same 52px gun and the same 30px wheel it spawned with — sitting in exactly the right place
 * and looking like toys on a barge. It is also the same multiplication `Cannon.drawn` does on the
 * simulation's side of the wire, and the two have to agree or a muzzle flash is the wrong size for
 * the gun it comes out of.
 */
function drawnSize(fitout: Fitout, size: number, slotScale: number): number {
    return size * slotScale * fitout.scale;
}

/**
 * Hangs one fitting on the deck and hands it back, or null for art the sheets do not have —
 * see `FALLBACK_HULL`. Only the guns keep the reference; everything else is placed and forgotten.
 */
function add(
    container: Container,
    animated: AnimatedSprite[],
    art: string,
    at: { x: number; y: number; rotation: number },
    length: number,
    size: { halfBeam: number; halfLength: number },
    anchorY: number,
    alpha: number
): Sprite | null {
    const sprite = place(art, at, length, size, anchorY);
    if (!sprite) return null;

    sprite.alpha = alpha;
    container.addChild(sprite);
    if (sprite instanceof AnimatedSprite) animated.push(sprite);

    return sprite;
}

/**
 * The frames one art reference plays, in order: an **animation** if either sheet knows the
 * name, a single frame if one is cached under it, and nothing at all if the tree points at art
 * the sheets do not have. That last case is a tree mid-edit, and the caller draws nothing.
 *
 * A plain frame name is a one-frame animation, which is what lets every level in the tree
 * store the same kind of string whether its art moves or not. `editor/atlas.ts` resolves an
 * art reference the same way, so the dashboard and the arena compose the same ship.
 */
export function framesOf(name: string): Texture[] {
    for (const sheet of ART_SHEETS) {
        const frames = Assets.get<Spritesheet>(sheet)?.animations[name];
        if (frames?.length) return frames;
    }

    // `Texture.from` is a cache lookup in v8 and returns undefined for a name nothing has
    // loaded, so the cache is asked first rather than the answer checked afterwards.
    return Cache.has(name) ? [Texture.from(name)] : [];
}

/**
 * One fitting hung on the hull: art at a hull-local point, turned to its own bearing and
 * scaled to an on-screen length.
 *
 * The position is the same half-beam / half-length box `mountPoint` works in — **this hull's**,
 * out of the fitout — so a gun is drawn exactly where the server thinks its muzzle is, and
 * `rotation` is degrees from the bow, the same zero a heading uses, because the art faces north
 * (design/Ships.md).
 *
 * **The scale comes from the first frame**, never the frame being drawn: a sail's three frames
 * are 154, 131 and 89 pixels tall untrimmed, and sizing each to `length` would pump the sail in
 * and out instead of billowing it. Everything is measured against the **untrimmed** height, so
 * a tightly cropped frame and a loosely cropped one come out the same size.
 */
function place(
    art: string,
    at: { x: number; y: number; rotation: number },
    length: number,
    size: { halfBeam: number; halfLength: number },
    anchorY: number
): Sprite | null {
    const frames = framesOf(art);
    if (frames.length === 0) return null;

    const anchor = { x: 0.5, y: anchorY };
    // Multi-frame art is driven by hand rather than by autoUpdate, for the same reason the wake
    // is: `Ticker.shared` is not the clock this game runs on.
    const sprite =
        frames.length > 1
            ? new AnimatedSprite({ textures: frames, anchor, autoUpdate: false })
            : new Sprite({ texture: frames[0], anchor });

    sprite.scale.set(length / frames[0].height);
    sprite.position.set(at.x * size.halfBeam, at.y * size.halfLength);
    sprite.rotation = at.rotation * DEG_TO_RAD;

    return sprite;
}
